import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { UseFormReturn } from "react-hook-form";

export interface BulkBillingFilters {
  period_start: string | null;
  period_end: string | null;
  page: number;
}

interface BulkBillingFiltersProps {
  filters: BulkBillingFilters;
  onFiltersChange: (filters: BulkBillingFilters) => void;
  onApplyFilters: (filters: BulkBillingFilters) => void;
  onClearFilters: () => void;
}

export const BulkBillingFilters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  onClearFilters,
}: BulkBillingFiltersProps) => {
  const FIELD_CLASSES = {
    container: "w-full mb-4",
    label: "block text-sm font-semibold text-gray-700 mb-2",
    input:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    select:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    textarea:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    error: "text-red-600 text-sm mt-1",
    helperText: "text-xs text-gray-500 mt-1",
  } as const;

  const createFormFields = (): FormField[] => [
    {
      name: "period_start",
      label: "Period Range - From",
      type: "datepicker",
      required: false,
      classes: FIELD_CLASSES,
      helperText: "Select start date for period filter",
    },
    {
      name: "period_end",
      label: "Period Range - To",
      type: "datepicker",
      required: false,
      classes: FIELD_CLASSES,
      helperText: "Select end date for period filter",
    },
  ];

  // Create form instance
  const form: UseFormReturn<BulkBillingFilters> =
    FormService.createForm<BulkBillingFilters>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: BulkBillingFilters) => {
    const currentValues = form.getValues();
    onApplyFilters({ ...currentValues, page: 1 });
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: BulkBillingFilters = {
      period_start: null,
      period_end: null,
      page: 1,
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="flex justify-between items-center pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={handleReset}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
        Clear Filters
      </button>

      <button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Apply Filters
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false,
          showResetButton: false,
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={2}
        responsiveColumns={{ sm: 1, md: 2 }}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
